import { Switch, Transition } from '@headlessui/react';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import DatePicker from 'react-datepicker';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';
import GamificationDetail from 'components/GamificationDetail';
import Notification from 'components/Notification';
import { addCommas, removeNonNumeric } from 'utils/NumberHelper';
import { isGamificationVisible } from 'utils/network/ServiceHelper';
import TierItemForm from './components/TierItemForm';
import tierService from 'utils/network/tier/tier.service';
import gamificationService from 'utils/network/gamification/gamification.service';
import { useNotif } from 'context/NotificationContext';
import {
  BONUS_POINT,
  PAGINATION,
  POINT_TIER,
  STATUS_SUCCESS,
  STATUS_UNAUTHORIZED
} from 'utils/constants';

export default function Gamification() {
  const navigate = useNavigate();
  const columns = [
    {
      name: 'Start At',
      selector: 'valid_from',
      cell: function render(row) {
        return <>{moment(row.valid_from).format('DD MMMM YYYY HH:mm')}</>;
      }
    },
    {
      name: 'End At',
      selector: 'valid_to',
      cell: function render(row) {
        return <>{moment(row.valid_to).format('DD MMMM YYYY HH:mm')}</>;
      }
    },
    {
      name: 'Created At',
      sortable: true,
      selector: 'created_at',
      cell: function render(row) {
        return (
          <span className="text-gray-400">
            {moment(row.created_at).format('DD MMMM YYYY')}
          </span>
        );
      }
    },
    {
      name: 'Created By',
      sortable: true,
      selector: 'created_by',
      cell: function render(row) {
        return <span className="text-gray-400">{row.created_by}</span>;
      }
    },
    {
      name: 'Actions',
      cell: function render(row) {
        return (
          <div className="w-full">
            <button
              className="p-1 text-center bg-gray-100 border rounded-lg leading-5 transition-colors duration-150 active:bg-purple-600 hover:bg-gray-300 focus:outline-none focus:shadow-outline-gray"
              onClick={handleCreateEdit(row)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </button>
            <button
              className="p-1 ml-2 text-center bg-gray-100 border rounded-lg leading-5 transition-colors duration-150 active:bg-purple-600 hover:bg-gray-300 focus:outline-none focus:shadow-outline-gray"
              onClick={handleDelete(row)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          </div>
        );
      }
    }
  ];

  const { setNotification, notification } = useNotif();
  const [pagination, setPagination] = useState({
    page: PAGINATION.DEFAULT_PAGE,
    perPage: PAGINATION.DEFAULT_PER_PAGE,
    totalPage: PAGINATION.DEFAULT_TOTAL_PAGE,
    totalData: PAGINATION.DEFAULT_TOTAL_DATA
  });
  const [form, setForm] = useState({
    id: null,
    start_at: new Date(),
    end_at: new Date(),
    tiers: {},
    set_default_points: false,
    default_points: ['', '', '', '', '', '', '', '']
  });
  const [createEdit, setCreateEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showStartDate, setShowStartDate] = useState(false);
  const [showEndDate, setShowEndDate] = useState(false);
  const [sortProp, setSortProp] = useState({
    order: 'created_at',
    sort: 'desc'
  });
  const [isVisible, setIsVisible] = useState(false);
  const [showVisibility, setShowVisibility] = useState(false);

  const { data } = useQuery({
    queryKey: ['tier-list'],
    queryFn: () => {
      return tierService.list({
        limit: 100,
        tier_name: '',
        offset: 1,
        order: 'created_at',
        sort: 'desc'
      });
    }
  });

  const query = useQueryClient();
  const queryKey = useMemo(() => {
    return `tier-list--${JSON.stringify({
      page: pagination.page,
      perPage: pagination.perPage,
      search: pagination.search,
      sortProp
    })}`;
  }, [pagination.page, pagination.perPage, pagination.search, sortProp]);

  // get list
  const { data: gamificationData, isLoading: getGamificationLoading } =
    useQuery({
      queryKey,
      queryFn: () => {
        return gamificationService.list({
          limit: pagination.perPage,
          tier_name: pagination.search,
          offset: pagination.page,
          order: sortProp.order,
          sort: sortProp.sort
        });
      }
    });

  // create or update
  const { mutate: createUpdateGamification } = useMutation(
    (body) => {
      if (body.id) {
        return gamificationService.put(body);
      }
      return gamificationService.post(body);
    },
    {
      onSuccess: (data) => {
        if (data?.status === STATUS_SUCCESS) {
          setNotification({
            ...notification,
            show: true,
            success: true,
            title: 'Success',
            message: `Gamification successfully ${
              form?.id ? 'updated' : 'created'
            }`
          });
          query.invalidateQueries([queryKey]);
          closeCreateEdit();
        }
      },
      onError: (error) => {
        setNotification({
          ...notification,
          show: true,
          title: 'Error',
          error: true,
          message: error?.response?.data?.message
        });
      }
    }
  );

  // delete
  const { mutate: deleteGamification } = useMutation(
    (id) => {
      return gamificationService.delete(id);
    },
    {
      onSuccess: (data) => {
        if (data?.status === STATUS_SUCCESS) {
          query.invalidateQueries([queryKey]);
          closeDelete();
          setNotification({
            ...notification,
            success: true,
            show: true,
            title: 'Success',
            message: 'Gamification successfully deleted'
          });
        }
      },
      onError: () => {
        setNotification({
          ...notification,
          show: true,
          title: 'Error',
          error: true,
          message: 'Error delete gamification'
        });
      }
    }
  );

  async function getIsGamificationVisible() {
    const baseUrl = process.env.REACT_APP_BASE_URL;

    try {
      const res = await isGamificationVisible(baseUrl, 'GET');

      if (res?.status === STATUS_SUCCESS) {
        setIsVisible(res.data.is_visible);
        return;
      }

      if (res?.status === STATUS_UNAUTHORIZED) {
        localStorage.clear();
        navigate('/login', { replace: true });
        return;
      }

      const err = JSON.parse(await res.text());
      if (err) {
        notif('Error', err.message, true);
        return;
      }

      notif('Error', 'Network error. Please try again later!', true);
    } catch (error) {
      notif('Error', 'Network error. Please try again later!', true);
    }
  }

  async function populateTiers() {
    const copyForm = { ...form };
    data?.data?.forEach((tier) => {
      copyForm.tiers[`${tier.tier_name}(||?)${tier.id}`] = [
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        ''
      ];
    });
    setForm(copyForm);
  }

  function getTierName(tierKey) {
    const splitted = tierKey.split('(||?)');
    return splitted?.[0] ?? '';
  }

  function getTierId(tierKey) {
    const splitted = tierKey.split('(||?)');
    return splitted?.[1] ?? '';
  }

  useEffect(() => {
    getIsGamificationVisible();
  }, []);

  useEffect(() => {
    if (data?.data) {
      populateTiers();
    }
  }, [data]);

  function handlePerRowsChange(newPerPage, page) {
    setPagination({
      ...pagination,
      page,
      perPage: newPerPage,
      totalPage: Math.ceil(
        (gamificationData?.data?.total_data || 0) / newPerPage
      )
    });
  }

  function handlePageChange(page) {
    setPagination({
      ...pagination,
      page
    });
  }

  const handleSort = useCallback((column, sortDirection) => {
    setSortProp({
      order: column.selector,
      sort: sortDirection
    });
  });

  const handleCreateEdit = (row) => () => {
    if (!data?.data) return;
    if (row) {
      const tiers = {};
      let default_points = ['', '', '', '', '', '', '', ''];
      if (row.is_point_tier === POINT_TIER.YES) {
        Object.keys(form.tiers).forEach((key) => {
          const tierId = +getTierId(key);
          const points = [];
          row.gamification_point_map.forEach((mapKey) => {
            if (mapKey?.tier_id === tierId) {
              Object.keys(mapKey).forEach((v) => {
                if (v.startsWith('point_')) {
                  points.push(mapKey[v]?.toString());
                }
              });
              points.push(mapKey.bonus_point?.toString());
            }
          });
          tiers[key] = points;
        });
      } else {
        const points = [];
        row.gamification_point_map.forEach((mapKey) => {
          Object.keys(mapKey).forEach((v) => {
            if (v.startsWith('point_')) {
              points.push(mapKey[v]?.toString());
            }
          });
          points.push(mapKey.bonus_point?.toString());
        });
        default_points = points;
      }
      const copyForm = {
        ...form,
        id: row.id,
        start_at: new Date(row.valid_from),
        end_at: new Date(row.valid_to),
        default_points,
        tiers
      };
      if (Object.keys(tiers)?.length === 0) {
        data?.data?.forEach((tier) => {
          copyForm.tiers[`${tier.tier_name}(||?)${tier.id}`] = [
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            ''
          ];
        });
      }
      setForm(copyForm);
    }
    setCreateEdit(true);
  };

  function closeCreateEdit() {
    setCreateEdit(false);
  }

  const handleDelete = (row) => () => {
    setForm({ ...form, id: row.id });
    setShowDelete(true);
  };

  function closeDelete() {
    setShowDelete(false);
  }

  function handlePointFormChange(e, key) {
    const newValue = e.target.value.trim();
    if (newValue.startsWith('0')) {
      return;
    }
    setForm({
      ...form,
      tiers: {
        ...form.tiers,
        [key]: form.tiers[key].map((x, i) => {
          if (i === parseInt(e.target.name)) {
            return addCommas(removeNonNumeric(e.target.value));
          }

          return x;
        })
      }
    });
  }

  function handleDefaultPointFormChange(e) {
    const newValue = e.target.value.trim();
    if (newValue.startsWith('0')) {
      return;
    }

    setForm({
      ...form,
      default_points: form.default_points.map((x, idx) => {
        if (idx === parseInt(e.target.name)) {
          return addCommas(removeNonNumeric(e.target.value));
        }

        return x;
      })
    });
  }

  const handleDateChange = (name) => (date) => {
    setForm({
      ...form,
      [name]: date
    });
  };

  function notif(title, message, isError = false, duration = 3000) {
    setNotification({
      show: true,
      title: title,
      message: message,
      error: isError
    });

    setTimeout(() => {
      setNotification({ ...notification, show: false });
    }, duration);
  }

  function closeNotification() {
    setNotification({ ...notification, show: false });
  }

  function handleVisibility() {
    setShowVisibility(true);
  }

  function closeVisibility() {
    setShowVisibility(false);
  }

  function isValid() {
    let isPointValid = true;

    if (form.set_default_points) {
      form.default_points.forEach((x, i) => {
        if (!x && i !== BONUS_POINT) {
          isPointValid = false;
        }
      });
    } else {
      Object.keys(form.tiers).forEach((key) => {
        form.tiers[key].forEach((v, i) => {
          if (!v && i !== BONUS_POINT) {
            isPointValid = false;
          }
        });
      });
    }

    return isPointValid;
  }

  function isDateValid() {
    if (form.start_at.getTime() > form.end_at.getTime()) {
      notif('Error', 'The end at cannot be earlier than the start time', true);
      return false;
    }

    return true;
  }

  useEffect(() => {
    if (!createEdit && !showDelete && data?.data) {
      const copyForm = {
        ...form,
        id: null,
        start_at: new Date(),
        end_at: new Date(),
        default_points: ['', '', '', '', '', '', '', '']
      };
      data?.data?.forEach((tier) => {
        copyForm.tiers[`${tier.tier_name}(||?)${tier.id}`] = [
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          ''
        ];
      });
      setForm(copyForm);
      setShowStartDate(false);
      setShowEndDate(false);
    }
  }, [createEdit, showDelete]);

  useEffect(() => {
    if (showStartDate) setShowEndDate(false);
    if (showEndDate) setShowStartDate(false);
  }, [showStartDate, showEndDate]);

  async function handleCreateEditGamificationPoint() {
    if (!isValid()) {
      notif('Error', 'Please fill all required field!', true);
      return;
    }

    if (!isDateValid()) {
      return;
    }

    try {
      form.is_point_tier = !form.set_default_points
        ? POINT_TIER.YES
        : POINT_TIER.NO;
      if (form.set_default_points) {
        form.default_point = form.default_points?.map((x, i) => {
          const index = i + 1;
          return {
            index,
            value: +removeNonNumeric(x)
          };
        });
      } else {
        delete form.default_point;
        form.gamification_point_tier = Object.keys(form.tiers).map((key) => {
          return {
            tier_id: +getTierId(key),
            point: form.tiers?.[key]?.map((point, i) => {
              const idx = i + 1;
              return {
                index: idx,
                value: +removeNonNumeric(point)
              };
            })
          };
        });
      }
      const obj = { ...form };
      obj.valid_from = obj.start_at;
      obj.valid_to = obj.end_at;

      delete obj.tiers;
      delete obj.set_default_points;
      delete obj.default_points;
      delete obj.point;
      delete obj.start_at;
      delete obj.end_at;

      createUpdateGamification(obj);
    } catch (error) {
      notif('Error', 'Network error. Please try again later!', true);
    }
  }

  async function handleDeletePoint() {
    deleteGamification(form.id);
  }

  async function toggleGamification() {
    const baseUrl = process.env.REACT_APP_BASE_URL;

    try {
      const res = await isGamificationVisible(baseUrl, 'PUT');

      if (res?.status === STATUS_SUCCESS) {
        notif('Success', 'Gamification Button visibility sucessfully changed!');

        closeVisibility();

        getIsGamificationVisible();
        return;
      }

      if (res.status === STATUS_UNAUTHORIZED) {
        localStorage.clear();
        navigate('/login', { replace: true });
        return;
      }

      const error = JSON.parse(await res.text());
      if (error) {
        notif(
          'Error',
          error?.message ?? 'Network error. Please try again later!',
          true
        );
        return;
      }

      notif(
        'Error',
        res?.message ?? 'Network error. Please try again later!',
        true
      );
    } catch (error) {
      notif('Error', 'Network error. Please try again later!', true);
    }
  }

  return (
    <div>
      <div className="flex flex-row items-center justify-between">
        <h2 className="my-6 text-xl font-semibold text-gray-700 dark:text-gray-200">
          Gamification
        </h2>
        <div className="flex flex-row items-center justify-center">
          <button
            className="px-4 py-2 mt-3 mr-3 text-sm font-medium text-center bg-white border border-yellow-500 rounded-lg text-secondary leading-5 text-purple transition-colors duration-150 active:bg-yellow-600 hover:bg-yellow-100 focus:outline-none focus:shadow-outline-purple"
            onClick={handleVisibility}
          >
            {isVisible
              ? 'Disable Button Gamification'
              : 'Enable Button Gamification'}
          </button>
          <button
            className="px-4 py-2 mt-3 text-sm font-medium text-center text-white border border-transparent rounded-lg leading-5 transition-colors duration-150 bg-secondary hover:bg-yellow-700 focus:outline-none focus:shadow-outline-purple"
            onClick={handleCreateEdit(null)}
          >
            Add New
          </button>
        </div>
      </div>
      <DataTable
        columns={columns}
        data={gamificationData?.data?.data || []}
        sortServer
        onSort={handleSort}
        progressPending={getGamificationLoading}
        pagination
        paginationServer
        paginationTotalRows={gamificationData?.data?.total_data}
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
        expandableRows
        expandableRowsComponent={GamificationDetail}
      />

      {/* Create Edit Section */}
      <Transition
        show={createEdit}
        enter="transition-all ease-in duration-100"
        enterFrom="opacity-0"
        enterTo="opacity-100"
      >
        <div className="fixed inset-0 z-20 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0">
              <div className="absolute inset-0 bg-gray-100 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen"></span>
            <div
              className="inline-block px-8 pt-5 pb-8 overflow-hidden text-left align-bottom bg-white rounded-lg shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-headline"
            >
              {form.id ? (
                <h3 className="mb-3 font-bold">Edit Point</h3>
              ) : (
                <h3 className="mb-3 font-bold">Create Point</h3>
              )}

              <div
                className="absolute cursor-pointer top-3 right-3"
                onClick={closeCreateEdit}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              {form.id ? (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <h2 className="text-sm">Start At</h2>
                    <p className="text-gray-400">
                      {moment(form.start_at).format('DD MMMM yyyy HH:mm')}
                    </p>
                  </div>
                  <div>
                    <h2 className="text-sm">End At</h2>
                    <p className="text-gray-400">
                      {moment(form.end_at).format('DD MMMM yyyy HH:mm')}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <label className="block text-sm">
                    <span className="text-gray-700 dark:text-gray-400">
                      Start At <span className="text-red-500">*</span>
                    </span>
                    <div className="relative">
                      <DatePicker
                        selected={form.start_at}
                        name="start_at"
                        dateFormat="dd MMMM yyyy HH:mm"
                        onChange={handleDateChange('start_at')}
                        minDate={new Date()}
                        showTimeSelect
                        portalId="root-portal"
                        nextMonthButtonLabel={
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-5 h-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        }
                        previousMonthButtonLabel={
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-5 h-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 19l-7-7 7-7"
                            />
                          </svg>
                        }
                      />
                      <div className="absolute top-0 bottom-0 flex flex-col justify-center text-gray-400 cursor-pointer right-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                    </div>
                  </label>
                  <label className="block text-sm">
                    <span className="text-gray-700 dark:text-gray-400">
                      End At <span className="text-red-500">*</span>
                    </span>
                    <div className="relative">
                      <DatePicker
                        selected={form.end_at}
                        name="end_at"
                        dateFormat="dd MMMM yyyy HH:mm"
                        onChange={handleDateChange('end_at')}
                        minDate={form.start_at}
                        showTimeSelect
                        portalId="root-portal"
                        nextMonthButtonLabel={
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-5 h-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        }
                        previousMonthButtonLabel={
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-5 h-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 19l-7-7 7-7"
                            />
                          </svg>
                        }
                      />
                      <div className="absolute top-0 bottom-0 flex flex-col justify-center text-gray-400 cursor-pointer right-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                    </div>
                  </label>
                </div>
              )}
              <div>
                <div className="flex flex-row justify-between items-center">
                  <label className="flex flex-row gap-3 items-center mt-4">
                    <Switch
                      checked={form.set_default_points}
                      onChange={() =>
                        setForm({
                          ...form,
                          set_default_points: !form.set_default_points
                        })
                      }
                      className={`${
                        form.set_default_points ? 'bg-gray-500' : 'bg-gray-200'
                      }
          relative inline-flex flex-shrink-0 w-10 h-5 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus-visible:ring-2  focus-visible:ring-white focus-visible:ring-opacity-75`}
                    >
                      <span className="sr-only">Set Default Point</span>
                      <span
                        aria-hidden="true"
                        className={`${
                          form.set_default_points
                            ? 'translate-x-5'
                            : 'translate-x-0'
                        }
            pointer-events-none inline-block w-4 h-4 rounded-full bg-white shadow-lg transform ring-0 transition ease-in-out duration-200`}
                      />
                    </Switch>
                    <span className="text-gray-700 dark:text-gray-400">
                      Set Default Point
                    </span>
                  </label>
                </div>
                {form.set_default_points && (
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    {form.default_points.map((x, idx) => {
                      return (
                        <label key={idx.toString()} className="block text-sm">
                          <span className="text-gray-700 dark:text-gray-400">
                            {idx === BONUS_POINT ? (
                              <>Bonus Poin </>
                            ) : (
                              <>
                                Hari ke {`${idx + 1}`}&nbsp;
                                <span className="text-red-500">*</span>
                              </>
                            )}
                          </span>
                          <input
                            className="block w-full px-3 py-2 mt-1 text-sm border appearance-none rounded-md dark:border-gray-600 dark:bg-gray-700 focus:border-purple-400 focus:outline-none focus:shadow-outline-purple dark:text-gray-300 dark:focus:shadow-outline-gray form-input"
                            placeholder="0"
                            name={`${idx}`}
                            value={x}
                            // type="number"
                            onChange={handleDefaultPointFormChange}
                          />
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              <div>
                {form && form.tiers && !form.set_default_points
                  ? Object.keys(form.tiers).map((key) => (
                      <TierItemForm
                        key={key}
                        name={getTierName(key)}
                        tiers={form.tiers[key]}
                        tierKey={key}
                        onChange={handlePointFormChange}
                      />
                    ))
                  : null}
              </div>
              <div className="flex flex-row-reverse">
                <button
                  className="px-4 py-2 mt-3 text-sm font-medium text-center text-white border border-transparent rounded-lg leading-5 transition-colors duration-150 bg-secondary hover:bg-yellow-700 focus:outline-none focus:shadow-outline-purple"
                  onClick={handleCreateEditGamificationPoint}
                >
                  {form.id ? 'Edit' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>

      {/* Delete Section */}
      <Transition
        show={showDelete}
        enter="transition-all ease-in duration-100"
        enterFrom="opacity-0"
        enterTo="opacity-100"
      >
        <div className="fixed inset-0 z-20 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0">
              <div className="absolute inset-0 bg-gray-100 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen"></span>

            <div
              className="inline-block p-8 overflow-hidden text-left align-bottom bg-white rounded-lg shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-headline"
            >
              <h3 className="font-bold">Delete Confirmation</h3>
              <div
                className="absolute cursor-pointer top-3 right-3"
                onClick={closeDelete}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <p>Are you sure to delete this item?</p>
              <div className="flex flex-row-reverse">
                <button
                  className="px-4 py-2 mt-3 text-sm font-medium text-center text-white border border-transparent rounded-lg leading-5 transition-colors duration-150 bg-secondary hover:bg-yellow-700 focus:outline-none focus:shadow-outline-purple"
                  onClick={closeDelete}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 mt-3 text-sm font-medium text-center border border-transparent rounded-lg leading-5 transition-colors duration-150 focus:outline-none focus:shadow-outline-purple"
                  onClick={handleDeletePoint}
                >
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>

      {/* Gamification Button Visibility Section */}
      <Transition
        show={showVisibility}
        enter="transition-all ease-in duration-100"
        enterFrom="opacity-0"
        enterTo="opacity-100"
      >
        <div className="fixed inset-0 z-20 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0">
              <div className="absolute inset-0 bg-gray-100 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen"></span>

            <div
              className="inline-block p-8 overflow-hidden text-left align-bottom bg-white rounded-lg shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-headline"
            >
              <h3 className="font-bold">Change Visibility Confirmation</h3>
              <div
                className="absolute cursor-pointer top-3 right-3"
                onClick={closeVisibility}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <p>Are you sure to change Gamification Button visibility?</p>
              <div className="flex flex-row-reverse">
                <button
                  className="px-4 py-2 mt-3 text-sm font-medium text-center text-white border border-transparent rounded-lg leading-5 transition-colors duration-150 bg-secondary hover:bg-yellow-700 focus:outline-none focus:shadow-outline-purple"
                  onClick={closeVisibility}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 mt-3 text-sm font-medium text-center border border-transparent rounded-lg leading-5 transition-colors duration-150 focus:outline-none focus:shadow-outline-purple"
                  onClick={toggleGamification}
                >
                  Yes, Change
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>

      <Notification
        notification={notification}
        closeNotification={closeNotification}
      />
    </div>
  );
}
